/**
 * PRD.305 — the ONE ordering drawer, shared by the Laboratory module, Patient
 * Workspace Labs and Consultation Labs.
 *
 * Wherever it is opened from, it writes the same canonical order and the order
 * lands in the same Laboratory queue — the PRD's requirement that clinical
 * ordering and laboratory operations share one model, not three.
 *
 * The catalog is `appointment_services` rows flagged `is_lab_test`. Free-text
 * tests are allowed for one-offs; they simply carry no `service_id` and so are
 * not billable against a catalog price.
 *
 * Billing is OPTIONAL and explicit. PRD §4 lets an order exist before payment
 * where the clinic's own rules allow it, so this drawer never forces an
 * invoice — it offers one, reusing the existing billing subsystem.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { qLabTestCatalog, type LabTestCatalogRow } from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { createLabOrder, billLabTests, type LabTestDraft } from '../../services/laboratory-service';
import { LabEmptyState } from './lab-shared';

export interface LabOrderContext {
  patientId: string;
  patientName: string;
  clinicId?: string | null;
  visitId?: string | null;
  encounterId?: string | null;
  appointmentId?: string | null;
  queueId?: string | null;
  orderingProviderId?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: LabOrderContext | null;
  /** Which surface raised the order — recorded on `lab_orders.source`. */
  source: 'consultation' | 'patient_workspace' | 'laboratory';
  /** Whether the current user may add the tests to an invoice. */
  canBill?: boolean;
  onCreated?: (orderId: string) => void;
}

export function CreateLabOrderDrawer({
  open,
  onOpenChange,
  context,
  source,
  canBill = false,
  onCreated,
}: Props) {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const formatMoney = useFormatMoney();

  const { data: catalog = [] } = usePowerSyncQuery<LabTestCatalogRow>(qLabTestCatalog());

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [freeText, setFreeText] = useState('');
  const [freeTextTests, setFreeTextTests] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [addToInvoice, setAddToInvoice] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setFreeText('');
    setFreeTextTests([]);
    setNotes('');
    setSearch('');
    setError(null);
    setAddToInvoice(canBill);
  }, [open, context?.patientId, canBill]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (t) =>
        (t.name ?? '').toLowerCase().includes(q) ||
        (t.category_name ?? '').toLowerCase().includes(q),
    );
  }, [catalog, search]);

  const totalCents = useMemo(
    () =>
      catalog
        .filter((t) => selected.has(t.id))
        .reduce((sum, t) => sum + Number(t.price_cents ?? 0), 0),
    [catalog, selected],
  );

  const testCount = selected.size + freeTextTests.length;

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const addFreeText = useCallback(() => {
    const name = freeText.trim();
    if (!name) return;
    setFreeTextTests((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setFreeText('');
  }, [freeText]);

  const handleCreate = useCallback(async () => {
    if (!db || !context || busy || testCount === 0) return;
    setBusy(true);
    setError(null);
    try {
      const tests: LabTestDraft[] = [
        ...catalog
          .filter((t) => selected.has(t.id))
          .map((t) => ({
            serviceId: t.id,
            name: t.name ?? 'Laboratory test',
            priceCents: t.price_cents ?? null,
          })),
        ...freeTextTests.map((name) => ({ serviceId: null, name, priceCents: null })),
      ];

      const { orderId, testIds } = await createLabOrder({
        db,
        orgId,
        clinicId: context.clinicId ?? null,
        patientId: context.patientId,
        visitId: context.visitId ?? null,
        encounterId: context.encounterId ?? null,
        appointmentId: context.appointmentId ?? null,
        queueId: context.queueId ?? null,
        orderingProviderId: context.orderingProviderId ?? null,
        actor: { uid: user?.uid ?? '', name: user?.displayName ?? user?.email ?? null },
        source,
        tests,
        notes: notes.trim() || null,
      });

      if (canBill && addToInvoice) {
        await billLabTests({
          db,
          orgId,
          patientId: context.patientId,
          patientName: context.patientName,
          testIds,
          encounterId: context.encounterId ?? null,
          visitId: context.visitId ?? null,
          date: new Date().toISOString().slice(0, 10),
        });
      }

      onCreated?.(orderId);
      onOpenChange(false);
    } catch (err) {
      console.error('[CreateLabOrder] failed:', err);
      setError(err instanceof Error ? err.message : 'Could not create the laboratory order.');
    } finally {
      setBusy(false);
    }
  }, [
    db,
    context,
    busy,
    testCount,
    catalog,
    selected,
    freeTextTests,
    orgId,
    user,
    source,
    notes,
    canBill,
    addToInvoice,
    onCreated,
    onOpenChange,
  ]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="New Lab Order"
      description={context ? context.patientName : undefined}
      width={520}
      portalStyle={brandTokens}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--sp-3)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            {testCount === 0
              ? 'No tests selected'
              : `${testCount} test${testCount === 1 ? '' : 's'}${
                  totalCents > 0 ? ` · ${formatMoney(totalCents / 100)}` : ''
                }`}
          </span>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              size="md"
              onClick={handleCreate}
              disabled={busy || testCount === 0}
            >
              {busy ? 'Creating…' : 'Create Order'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fee2e2',
              color: '#991b1b',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search laboratory tests…"
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 'var(--font-size-sm)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {catalog.length === 0 ? (
          <LabEmptyState
            title="No laboratory tests in the catalog"
            description="Laboratory tests are services marked as a lab test. Add them under Appointments → Manage Services, then order them here. You can still add a one-off test below."
          />
        ) : (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            {filtered.map((t, i) => {
              const checked = selected.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => toggle(t.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--sp-3)',
                    background: checked ? 'var(--bg-surface-2)' : 'transparent',
                    border: 'none',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 'var(--radius-xs)',
                      border: `2px solid ${checked ? 'var(--color-primary)' : 'var(--border)'}`,
                      background: checked ? 'var(--color-primary)' : 'transparent',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text)',
                      }}
                    >
                      {t.name}
                    </span>
                    {t.category_name && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {t.category_name}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                    {t.price_cents != null ? formatMoney(t.price_cents / 100) : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* One-off test */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text)',
              marginBottom: 'var(--sp-2)',
            }}
          >
            Add a one-off test
          </label>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <input
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFreeText();
                }
              }}
              placeholder="Test name"
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 'var(--font-size-sm)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <Button variant="outline" size="md" onClick={addFreeText} disabled={!freeText.trim()}>
              Add
            </Button>
          </div>
          {freeTextTests.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--sp-2)',
                marginTop: 'var(--sp-2)',
              }}
            >
              {freeTextTests.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setFreeTextTests((prev) => prev.filter((n) => n !== name))}
                  title="Remove"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--sp-1)',
                    padding: '2px var(--sp-2)',
                    borderRadius: 'var(--radius-xs)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-surface-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  {name} ×
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text)',
              marginBottom: 'var(--sp-2)',
            }}
          >
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Clinical notes for the laboratory (optional)"
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 'var(--font-size-sm)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {canBill && (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--sp-2)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={addToInvoice}
              onChange={(e) => setAddToInvoice(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              Add these tests to the patient&rsquo;s invoice
              <span
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                }}
              >
                Uses the existing billing flow. Ordering without billing is allowed — the laboratory
                will still see the order.
              </span>
            </span>
          </label>
        )}
      </div>
    </Drawer>
  );
}

export default CreateLabOrderDrawer;
